import { useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children?: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  fullScreen?: boolean;
}

const SIZES = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
};

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

// close animation is intentionally a touch faster than the open one
const CLOSE_MS = 150;

export default function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  fullScreen = false,
}: ModalProps) {
  const [visible, setVisible] = useState(open);
  const [closing, setClosing] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);
  // keep the latest onClose without re-running the focus / scroll-lock effect
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // mount + exit animation
  useEffect(() => {
    if (open) {
      setVisible(true);
      setClosing(false);
      return undefined;
    }
    if (!visible) return undefined;
    setClosing(true);
    const timer = window.setTimeout(() => {
      setVisible(false);
      setClosing(false);
    }, CLOSE_MS);
    return () => window.clearTimeout(timer);
  }, [open, visible]);

  // scroll lock + focus management + keyboard handling
  useEffect(() => {
    if (!open) return undefined;

    restoreRef.current = (document.activeElement as HTMLElement) ?? null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusTimer = window.setTimeout(() => {
      const node = dialogRef.current;
      if (!node) return;
      const items = node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (items.length > 0) items[0].focus();
      else node.focus();
    }, 0);

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab') return;
      const node = dialogRef.current;
      if (!node) return;
      const items = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
      window.clearTimeout(focusTimer);
      restoreRef.current?.focus?.();
    };
  }, [open]);

  if (!visible) return null;

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 flex',
        fullScreen ? 'items-stretch justify-stretch' : 'items-center justify-center p-4',
      )}
    >
      <div
        className={cn(
          'absolute inset-0 bg-foreground-950/50 backdrop-blur-[2px]',
          closing ? 'animate-fade-out' : 'animate-fade-in',
        )}
        onMouseDown={() => onCloseRef.current()}
        aria-hidden="true"
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={cn(
          'relative flex w-full flex-col outline-none lb-glass-strong',
          closing ? 'animate-modal-out' : 'animate-modal-in',
          fullScreen ? 'h-full rounded-none' : cn('rounded-lg', SIZES[size]),
        )}
      >
        {(title || description) && (
          <div className="shrink-0 border-b border-background-200">
            <div
              className={cn(
                'flex items-start justify-between gap-4 px-5 py-4',
                fullScreen && 'mx-auto w-full max-w-4xl px-5 py-4 md:px-8',
              )}
            >
              <div>
                {title ? <h3 className="text-base font-semibold text-foreground-950">{title}</h3> : null}
                {description ? (
                  <p className="mt-0.5 text-sm text-foreground-600">{description}</p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="close"
                className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 transition-colors duration-150 ease-smooth hover:bg-background-100 cursor-pointer shrink-0"
              >
                <i className="ri-close-line text-xl" aria-hidden="true" />
              </button>
            </div>
          </div>
        )}
        {children ? (
          fullScreen ? (
            <div className="flex-1 overflow-y-auto">
              <div className="mx-auto w-full max-w-4xl px-5 py-6 md:px-8">{children}</div>
            </div>
          ) : (
            <div className="max-h-[65vh] overflow-y-auto px-5 py-4">{children}</div>
          )
        ) : null}
        {footer ? (
          <div className="shrink-0 border-t border-background-200">
            <div
              className={cn(
                'flex flex-wrap items-center justify-end gap-2 px-5 py-3.5',
                fullScreen && 'mx-auto w-full max-w-4xl px-5 py-4 md:px-8',
              )}
            >
              {footer}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}